import { useState, type ReactNode } from "react";

import { FilePick } from "@/components/FilePick";

import { ActionRow } from "@/components/ActionRow";
/** The bulk-CSV flow (U7): reference-sheet strip (note + template link) ·
 *  divider · right-aligned action line (filename · Choose · extra controls ·
 *  count-labelled action button). Built twice by hand (Enroll + Allocate) —
 *  every alignment fix used to be applied twice.
 *
 *  Parsing happens client-side on file pick; the parsed items go to the page
 *  via `onParsed` (for preview tables) and back into `onSubmit` on action. */
export function CsvUpload<T>({
  referenceNote, templateUrl, parse, onParsed, onSubmit,
  actionLabel, disabled = false, busy = false, controls, error,
}: {
  /** Optional: a caller with nothing to add should not have to add nothing.
   *  The template link already names the file; a paragraph describing its
   *  columns pushes the real controls down the panel. */
  referenceNote?: ReactNode;
  templateUrl: string;
  parse: (text: string) => T[];
  onParsed: (items: T[] | null) => void;
  onSubmit: (items: T[]) => void;
  /** e.g. (n) => `Enroll ${n}` */
  actionLabel: (n: number) => string;
  disabled?: boolean;
  busy?: boolean;
  /** extra controls between Choose and the action button (e.g. company select) */
  controls?: ReactNode;
  error?: ReactNode;
}) {
  const [fileName, setFileName] = useState("");
  const [items, setItems] = useState<T[] | null>(null);
  const [parseError, setParseError] = useState("");

  const onFile = async (f: File | null) => {
    setFileName(f?.name ?? "");
    setParseError("");
    let parsed: T[] | null = null;
    try {
      parsed = f ? parse(await f.text()) : null;
    } catch (e) {
      // strict parsers throw with row-numbered issues — show them, arm nothing
      setParseError(e instanceof Error ? e.message : String(e));
    }
    setItems(parsed);
    onParsed(parsed);
  };

  const n = items?.length ?? 0;
  return (
    <>
      {/* Part 1 — reference sheet. Rendered ONLY when there is a note to put
          in it: without one this was an empty eyebrow, a lone button and a
          divider — three rows of chrome to deliver one link. Callers with
          nothing to say place the link themselves, beside the settings that
          choose WHICH template, because those are the same thought. */}
      {referenceNote && (<>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".8px", textTransform: "uppercase", color: "var(--t-mute)" }}>Reference sheet</div>
            <div style={{ fontSize: 11.5, color: "var(--t-dim)", marginTop: 4 }}>{referenceNote}</div>
          </div>
          <a className="btn ghost" href={templateUrl}>⭳ Download template (.csv)</a>
        </div>

        <div style={{ height: 1, background: "var(--line)", margin: "16px 0" }} />
      </>)}

      {/* Part 2 — action line, right-aligned */}
      <ActionRow>
        <FilePick accept=".csv" label="Choose .csv" fileName={fileName} onFile={onFile} before />
        {controls}
        <button className="btn teal" disabled={n === 0 || disabled || busy}
          onClick={() => items && onSubmit(items)}>
          {actionLabel(n)}
        </button>
        {error}
      </ActionRow>
      {parseError && (
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--bad)", whiteSpace: "pre-line" }}>
          {parseError}
        </div>
      )}
    </>
  );
}
